import { CheckCircle, MessageSquarePlus, XCircle } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import { BlockMarkdown } from '@/components/core/markdown/markdown';
import { VisualBlock } from '@/components/core/visual';
import type { Question } from '@/data/fixtures';
import { correctAnswerText } from '@/lib/practice/answerText';
import { requestChatPrefill } from '@/lib/chat-prefill';

export interface QuizActionBarProps {
  question: Question;
  /** After submit: the verdict panel. Before: the Submit button. */
  revealed: boolean;
  correct: boolean;
  canSubmit: boolean;
  isLast: boolean;
  onSubmit: () => void;
  onNext: () => void;
}

/**
 * The quiz's bottom bar, pinned under the scrolling question - where a thumb
 * is, and where the next action always stays on screen.
 *
 * Before an answer it is just Submit. After, it becomes the feedback panel
 * (the pattern Duolingo made familiar): the verdict in the practice green or
 * destructive red, the right answer in words when yours was wrong, the
 * explanation, "Ask the tutor why" - a wrong answer is exactly when the
 * tutor is wanted, about THAT question - and Next. It replaces an inline
 * "Not quite Explanation" box that ran a status word into a heading and,
 * under a long explanation, pushed Next off the screen.
 */
export function QuizActionBar({
  question,
  revealed,
  correct,
  canSubmit,
  isLast,
  onSubmit,
  onNext,
}: QuizActionBarProps) {
  if (!revealed) {
    return (
      <div className='flex-shrink-0 border-t border-border bg-card px-4 py-3 sm:flex sm:justify-end sm:px-6'>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          className='h-11 w-full sm:h-9 sm:w-auto'
        >
          Submit answer
        </Button>
      </div>
    );
  }

  return (
    <FeedbackPanel
      question={question}
      correct={correct}
      isLast={isLast}
      onNext={onNext}
    />
  );
}

/** After submit: verdict, the right answer, why, a way to the tutor, Next. */
function FeedbackPanel({
  question,
  correct,
  isLast,
  onNext,
}: Pick<QuizActionBarProps, 'question' | 'correct' | 'isLast' | 'onNext'>) {
  const Icon = correct ? CheckCircle : XCircle;
  const answer = correctAnswerText(question);
  return (
    <div
      role='status'
      aria-label='Answer feedback'
      className={cn(
        'flex-shrink-0 border-t-2 px-4 pb-3 pt-3 sm:px-6',
        correct
          ? 'border-practice bg-practice/10'
          : 'border-destructive bg-destructive/10'
      )}
    >
      <div className='flex items-center gap-2'>
        <Icon
          className={cn(
            'h-5 w-5',
            correct ? 'text-practice-emphasis' : 'text-destructive-emphasis'
          )}
          aria-hidden='true'
        />
        <p
          className={cn(
            'text-base font-semibold',
            correct ? 'text-practice-emphasis' : 'text-destructive-emphasis'
          )}
        >
          {correct ? 'Correct' : 'Not quite'}
        </p>
      </div>
      {!correct && (
        <p className='mt-1 text-sm text-foreground'>
          <span className='font-medium'>Answer:</span> {answer}
        </p>
      )}
      {(question.explanation || question.explanationVisual) && (
        // Capped, so a long explanation scrolls inside the panel instead of
        // pushing Next below the screen - the concept's diagram scrolls with
        // it, and opens full size from its own button.
        <div
          className='mt-1.5 max-h-[24dvh] space-y-2 overflow-y-auto'
          data-testid='quiz-explanation'
        >
          {question.explanation && (
            <BlockMarkdown className='text-sm leading-relaxed text-foreground/85'>
              {question.explanation}
            </BlockMarkdown>
          )}
          {question.explanationVisual && (
            <VisualBlock
              visual={question.explanationVisual}
              compact
              caption={false}
            />
          )}
        </div>
      )}
      <div className='mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
        <button
          type='button'
          onClick={() =>
            requestChatPrefill(
              `Why is "${answer}" the answer to "${question.question}"? `
            )
          }
          className='flex min-h-11 items-center gap-1.5 self-start text-sm font-medium text-foreground underline-offset-2 hover:underline sm:min-h-9'
        >
          <MessageSquarePlus className='h-4 w-4' aria-hidden='true' />
          Ask the tutor why
        </button>
        <Button onClick={onNext} className='h-11 w-full sm:h-9 sm:w-auto'>
          {isLast ? 'Finish quiz' : 'Next question'}
        </Button>
      </div>
    </div>
  );
}
